import { useId, useState, type ReactElement } from 'react';
import { Chip } from '@/components/atoms/chip';
import { Button } from '@/components/atoms/button';
import { FormInput } from '@/components/molecules/form-input';
import { Micro } from '@/components/atoms/micro';
import {
  ABC_MAX_LENGTH,
  CONTRIBUTE_STRUCTURE_PRESETS,
  type ContributeSettingDraft,
} from '@/lib/contribute-form';
import type { Key } from '@/lib/types';

export type ContributeSettingsSectionProps = {
  settings: ContributeSettingDraft[];
  keys: readonly Key[];
  onAddSetting: () => void;
  onRemoveSetting: (draftId: string) => void;
  onUpdateSetting: (
    draftId: string,
    patch: Partial<
      Omit<ContributeSettingDraft, 'draftId' | 'isPrimary' | 'pendingFile' | 'pendingAudioFile'>
    >,
  ) => void;
  onMarkPrimary: (draftId: string) => void;
  onSelectFile: (draftId: string, file: File | null) => void;
  onSelectAudioFile: (draftId: string, file: File | null) => void;
};

export function ContributeSettingsSection({
  settings,
  keys,
  onAddSetting,
  onRemoveSetting,
  onUpdateSetting,
  onMarkPrimary,
  onSelectFile,
  onSelectAudioFile,
}: ContributeSettingsSectionProps): ReactElement {
  const primaryGroupName = useId();
  const canRemove = settings.length > 1;

  return (
    <div className="flex flex-col gap-4">
      {settings.map((setting, index) => (
        <SettingEntry
          key={setting.draftId}
          setting={setting}
          index={index}
          keys={keys}
          canRemove={canRemove}
          primaryGroupName={primaryGroupName}
          onRemoveSetting={onRemoveSetting}
          onUpdateSetting={onUpdateSetting}
          onMarkPrimary={onMarkPrimary}
          onSelectFile={onSelectFile}
          onSelectAudioFile={onSelectAudioFile}
        />
      ))}
      <div>
        <Button variant="ghost" type="button" onClick={onAddSetting}>
          Add another setting
        </Button>
      </div>
    </div>
  );
}

type SettingEntryProps = {
  setting: ContributeSettingDraft;
  index: number;
  keys: readonly Key[];
  canRemove: boolean;
  primaryGroupName: string;
  onRemoveSetting: (draftId: string) => void;
  onUpdateSetting: ContributeSettingsSectionProps['onUpdateSetting'];
  onMarkPrimary: (draftId: string) => void;
  onSelectFile: ContributeSettingsSectionProps['onSelectFile'];
  onSelectAudioFile: ContributeSettingsSectionProps['onSelectAudioFile'];
};

const AUDIO_FILE_ACCEPT =
  'audio/*,.mp3,.m4a,.wav,.ogg,.oga,.aac,.flac,.opus,.weba';

function SettingEntry({
  setting,
  index,
  keys,
  canRemove,
  primaryGroupName,
  onRemoveSetting,
  onUpdateSetting,
  onMarkPrimary,
  onSelectFile,
  onSelectAudioFile,
}: SettingEntryProps): ReactElement {
  const isPresetActive = (CONTRIBUTE_STRUCTURE_PRESETS as readonly string[]).includes(
    setting.structureLabel,
  );
  const [isCustomMode, setIsCustomMode] = useState(
    setting.structureLabel.length > 0 && !isPresetActive,
  );
  const [isAbcOpen, setIsAbcOpen] = useState(setting.abcNotation.length > 0);

  const groupLabel = `Setting ${index + 1}`;
  const nameId = useId();
  const keyId = useId();
  const customId = useId();
  const abcId = useId();
  const sheetMusicId = useId();
  const audioFileId = useId();

  return (
    <div
      role="group"
      aria-label={groupLabel}
      className="rounded border border-[var(--rule)] p-4 flex flex-col gap-3"
    >
      <div className="flex items-center justify-between">
        <Micro>{groupLabel}</Micro>
        {canRemove && (
          <Button
            variant="ghost"
            type="button"
            onClick={() => onRemoveSetting(setting.draftId)}
          >
            Remove this setting
          </Button>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <FormInput
          id={nameId}
          label="Setting name"
          value={setting.name}
          onChange={(event) => onUpdateSetting(setting.draftId, { name: event.target.value })}
        />

        <div className="flex flex-col gap-1.5">
          <Micro as="label" htmlFor={keyId} isRequired>
            Key
          </Micro>
          <select
            id={keyId}
            value={setting.keyId ?? ''}
            onChange={(event) =>
              onUpdateSetting(setting.draftId, { keyId: event.target.value || null })
            }
            className="h-11 rounded border border-[var(--rule)] bg-[var(--bg-surface)] px-3 text-sm text-[var(--ink)]"
          >
            <option value="">—</option>
            {keys.map((k) => (
              <option key={k.keyId} value={k.keyId}>
                {k.prettyName}
              </option>
            ))}
          </select>
        </div>

      </div>

      <div role="group" aria-label="Structure" className="flex flex-wrap gap-1.5">
        {CONTRIBUTE_STRUCTURE_PRESETS.map((preset) => (
          <Chip
            key={preset}
            active={!isCustomMode && setting.structureLabel === preset}
            aria-pressed={!isCustomMode && setting.structureLabel === preset}
            onClick={() => {
              setIsCustomMode(false);
              onUpdateSetting(setting.draftId, { structureLabel: preset });
            }}
          >
            {preset}
          </Chip>
        ))}
        <Chip
          active={isCustomMode}
          aria-pressed={isCustomMode}
          onClick={() => {
            setIsCustomMode(true);
            onUpdateSetting(setting.draftId, { structureLabel: '' });
          }}
        >
          Custom
        </Chip>
      </div>

      {isCustomMode && (
        <FormInput
          id={customId}
          label="Custom structure"
          value={setting.structureLabel}
          onChange={(event) =>
            onUpdateSetting(setting.draftId, { structureLabel: event.target.value })
          }
        />
      )}

      <div className="flex flex-col gap-1.5">
        <button
          type="button"
          onClick={() => setIsAbcOpen((open) => !open)}
          aria-expanded={isAbcOpen}
          aria-controls={abcId}
          className="flex items-center justify-between text-left text-sm text-[var(--ink)]"
        >
          <span>{isAbcOpen ? 'Hide ABC notation' : 'Show ABC notation'}</span>
          <span className="text-[10px] text-[var(--ink-3)]">
            {setting.abcNotation.length}/{ABC_MAX_LENGTH}
          </span>
        </button>
        {isAbcOpen && (
          <textarea
            id={abcId}
            aria-label="ABC notation"
            value={setting.abcNotation}
            onChange={(event) =>
              onUpdateSetting(setting.draftId, { abcNotation: event.target.value })
            }
            maxLength={ABC_MAX_LENGTH}
            rows={6}
            className="rounded border border-[var(--rule)] bg-[var(--bg-surface)] px-3 py-2 text-sm font-mono text-[var(--ink)] focus:outline-none focus:border-[var(--ink)]"
          />
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Micro as="label" htmlFor={sheetMusicId}>
          Sheet music (PDF)
        </Micro>
        <input
          id={sheetMusicId}
          type="file"
          accept=".pdf,application/pdf"
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null;
            onSelectFile(setting.draftId, file);
          }}
          className="text-sm text-[var(--ink)]"
        />
        {setting.pendingFile != null && setting.pendingFile instanceof File && (
          <div className="flex items-center gap-2 text-xs text-[var(--ink-2)]">
            <span>{setting.pendingFile.name}</span>
            <Button
              variant="ghost"
              type="button"
              onClick={() => onSelectFile(setting.draftId, null)}
            >
              Remove attached PDF
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Micro as="label" htmlFor={audioFileId}>
          Audio recording
        </Micro>
        <input
          id={audioFileId}
          type="file"
          accept={AUDIO_FILE_ACCEPT}
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null;
            onSelectAudioFile(setting.draftId, file);
          }}
          className="text-sm text-[var(--ink)]"
        />
        {setting.pendingAudioFile != null && setting.pendingAudioFile instanceof File && (
          <div className="flex items-center gap-2 text-xs text-[var(--ink-2)]">
            <span>{setting.pendingAudioFile.name}</span>
            <Button
              variant="ghost"
              type="button"
              onClick={() => onSelectAudioFile(setting.draftId, null)}
            >
              Remove attached recording
            </Button>
          </div>
        )}
      </div>

      <label className="inline-flex items-center gap-2 text-sm text-[var(--ink)] cursor-pointer">
        <input
          type="radio"
          name={primaryGroupName}
          checked={setting.isPrimary}
          onChange={() => onMarkPrimary(setting.draftId)}
          className="h-4 w-4"
        />
        Primary
      </label>
    </div>
  );
}
